import { useMemo } from 'react';
import {
  type MoodEntry,
  MOOD_CONFIG,
  getMonthDays,
  getMonthName,
  formatDate,
} from '../api/mockData';

interface CalendarProps {
  year: number;
  month: number;
  entries: Record<string, MoodEntry>;
  selectedDate?: string;
  onSelectDate: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export function Calendar({
  year,
  month,
  entries,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: CalendarProps) {
  const days = useMemo(() => getMonthDays(year, month), [year, month]);
  const today = formatDate(new Date());

  return (
    <div className="animate-fade-up" style={{ padding: '0 4px' }}>
      {/* Month nav */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        padding: '0 4px',
      }}>
        <button
          onClick={onPrevMonth}
          aria-label="Previous month"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--journal-bg-warm)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.1rem',
            color: 'var(--journal-ink-light)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--journal-line)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--journal-bg-warm)';
          }}
        >
          ←
        </button>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.3rem',
          fontWeight: 700,
          color: 'var(--journal-ink)',
          margin: 0,
        }}>
          {getMonthName(month)} {year}
        </h2>
        <button
          onClick={onNextMonth}
          aria-label="Next month"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--journal-bg-warm)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.1rem',
            color: 'var(--journal-ink-light)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--journal-line)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--journal-bg-warm)';
          }}
        >
          →
        </button>
      </div>

      {/* Day labels */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
        marginBottom: '4px',
      }}>
        {DAY_LABELS.map((label, i) => (
          <div key={i} style={{
            textAlign: 'center',
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'var(--journal-ink-faint)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '4px 0',
            fontFamily: 'var(--font-body)',
          }}>
            {label}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
      }}>
        {days.map((day, i) => {
          const dateStr = formatDate(day);
          const entry = entries[dateStr];
          const isCurrentMonth = day.getMonth() === month;
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const moodConfig = entry ? MOOD_CONFIG[entry.mood] : null;

          return (
            <button
              key={i}
              onClick={() => isCurrentMonth && onSelectDate(dateStr)}
              disabled={!isCurrentMonth}
              aria-label={`${day.toLocaleDateString()}, ${entry ? `mood: ${entry.mood}` : 'no entry'}`}
              style={{
                position: 'relative',
                aspectRatio: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
                border: isSelected ? '2px solid var(--journal-accent)' : '2px solid transparent',
                borderRadius: '12px',
                background: isSelected
                  ? 'var(--journal-bg-warm)'
                  : isToday
                    ? 'var(--journal-bg-warm)'
                    : 'transparent',
                cursor: isCurrentMonth ? 'pointer' : 'default',
                opacity: isCurrentMonth ? 1 : 0.25,
                transition: 'all 0.15s ease',
                padding: 0,
              }}
              onMouseEnter={(e) => {
                if (isCurrentMonth && !isSelected) {
                  e.currentTarget.style.background = 'var(--journal-bg-warm)';
                }
              }}
              onMouseLeave={(e) => {
                if (isCurrentMonth && !isSelected && !isToday) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{
                fontSize: '0.8rem',
                fontWeight: isToday ? 700 : 400,
                color: isToday ? 'var(--journal-accent)' : 'var(--journal-ink)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1,
              }}>
                {day.getDate()}
              </span>
              {moodConfig && (
                <span
                  className="animate-dot"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: moodConfig.color,
                    boxShadow: `0 1px 4px ${moodConfig.color}88`,
                    animationDelay: `${(i % 7) * 0.03}s`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
